import { FC } from 'react';

import { ImagesApp } from '../../constants/images';
import { useI18nContext } from '../../hooks/contexts/i18n.context';
import { useResaleContext } from '../../hooks/contexts/resale.context';
import { usePriceDisplay } from '../../hooks/usePriceDisplay';
import { formatEventDate } from '../../utils/formatEventDate';
import formatNumericValue from '../../utils/formatNumericValue';
import { BigButton } from '../Buttons';
import * as Styles from './styles';

type ResaleSummaryProps = {
  onProceed: () => void;
  disabled?: boolean;
  /**
   * Optional price override (e.g. promotional/discounted value).
   * When provided and strictly lower than `resaleData.saleValue`,
   * it is used as the displayed price and the original `saleValue`
   * is rendered alongside with a strikethrough. Otherwise ignored.
   */
  overridePrice?: number;
  /**
   * When true, a FastPass offer is being resolved. Renders a shimmer
   * `PriceSkeleton` placeholder for every price slot and disables the
   * proceed CTA, preventing the buyer from seeing the original price
   * (or submitting the checkout) before the offer resolves.
   *
   * Behaviour matches `ResaleSummaryMobile` for desktop/mobile parity.
   */
  offerPending?: boolean;
};

const ResaleSummary: FC<ResaleSummaryProps> = ({
  onProceed,
  disabled = false,
  overridePrice,
  offerPending = false
}) => {
  const { translate } = useI18nContext();
  const { resaleData } = useResaleContext();

  if (!resaleData) {
    return null;
  }

  const eventDate = new Date(resaleData.event.dates.from);
  const formattedDate = formatEventDate(eventDate);
  const fullDate = `${formattedDate.weekdayShort}, ${formattedDate.day} de ${formattedDate.monthFull} ` +
    `- ${formattedDate.time}`;

  const { displayPrice, hasDiscount } = usePriceDisplay(resaleData.saleValue, overridePrice);

  const formattedDisplayPrice = formatNumericValue({
    value : displayPrice,
    discount : 0,
    currencyFormat : 'BRL'
  });

  const formattedOriginalPrice = formatNumericValue({
    value : resaleData.saleValue,
    discount : 0,
    currencyFormat : 'BRL'
  });

  return (
    <Styles.Container id="resale-summary-container">
      <Styles.SummaryHeader id="resale-summary-header">
        <img src={ImagesApp.svg.summary} alt="summary" id="summary-header-img" />
        <h1 id="summary-header-title">{translate(['STEPS', 'TXT13'])}</h1>
      </Styles.SummaryHeader>

      <Styles.Detailing id="resale-summary-detailing">
        <Styles.SelectedTickets>
          <Styles.TicketItem>
            <Styles.TicketQuantity>1x</Styles.TicketQuantity>
            <Styles.TicketDetails>
              <Styles.TicketName>{resaleData.ticket.guestType.name}</Styles.TicketName>
              <Styles.TicketPrice>
                {offerPending ? (
                  <Styles.PriceSkeleton aria-label={translate(['FASTPASS', 'CALCULATING_PRICE'])} />
                ) : (
                  <>
                    {formattedDisplayPrice}
                    {hasDiscount && (
                      <Styles.StrikethroughPrice>{formattedOriginalPrice}</Styles.StrikethroughPrice>
                    )}
                  </>
                )}
              </Styles.TicketPrice>
              <Styles.TicketDate>{fullDate}</Styles.TicketDate>
            </Styles.TicketDetails>
            <Styles.TicketTotalPrice>
              {offerPending ? (
                <Styles.PriceSkeleton aria-label={translate(['FASTPASS', 'CALCULATING_PRICE'])} />
              ) : (
                <>
                  {formattedDisplayPrice}
                  {hasDiscount && (
                    <Styles.StrikethroughPrice>{formattedOriginalPrice}</Styles.StrikethroughPrice>
                  )}
                </>
              )}
            </Styles.TicketTotalPrice>
          </Styles.TicketItem>
        </Styles.SelectedTickets>
      </Styles.Detailing>

      <Styles.SummaryFooter id="resale-summary-footer">
        <Styles.RowFullWidth $align='space-between' id="summary-row-total-tickets">
          <Styles.TextInfo $weight={400} size={12} id="summary-total-tickets-label">
            {translate(['STEPS', 'TXT4'])}
          </Styles.TextInfo>
          <Styles.TextInfo $weight={700} size={12} id="summary-total-tickets-value">
            1
          </Styles.TextInfo>
        </Styles.RowFullWidth>

        <Styles.RowFullWidth $align='space-between' id="summary-row-total-value">
          <Styles.TextInfo $weight={400} size={12} id="summary-total-value-label">
            {translate(['STEPS', 'TXT14'])}
          </Styles.TextInfo>
          <Styles.TextInfo $weight={700} size={16} id="summary-total-value-amount">
            {offerPending ? (
              <Styles.PriceSkeleton aria-label={translate(['FASTPASS', 'CALCULATING_PRICE'])} />
            ) : (
              <>
                {formattedDisplayPrice}
                {hasDiscount && (
                  <Styles.StrikethroughPrice>{formattedOriginalPrice}</Styles.StrikethroughPrice>
                )}
              </>
            )}
          </Styles.TextInfo>
        </Styles.RowFullWidth>

        <Styles.RowFullWidth $align='center' id="summary-row-proceed-button">
          <BigButton
            disabled={disabled || offerPending}
            onClick={onProceed}
            id="resale-summary-proceed-button"
          >
            {translate(['STEPS', 'TXT15'])}
          </BigButton>
        </Styles.RowFullWidth>
      </Styles.SummaryFooter>
    </Styles.Container>
  );
};

export default ResaleSummary;
